import BackToCPQButton from "../../Components/backToCPQButton";
import PreviousPageButtonCPQ from "../../Components/PreviousPageButtonCPQ";

import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    Button,
    IconButton,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    TextField,
    Box,
    Grid,
    Snackbar,
    Alert,
    CircularProgress,
    InputAdornment,
    Tooltip,
    Divider,
} from "@mui/material";
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Search as SearchIcon,
    FilterAlt as FilterIcon,
    Clear as ClearIcon,
} from "@mui/icons-material";
import apiClient from "../../Helpers/apiClient";

function PipeWorkPriceTable() {
    const [pipeWorks, setPipeWorks] = useState([]);
    const [filteredPipeWorks, setFilteredPipeWorks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [openDialog, setOpenDialog] = useState(false);
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [selectedPipeWork, setSelectedPipeWork] = useState(null);
    const [formData, setFormData] = useState({
        id: 0,
        size: 0,
        material: "",
        price: 0,
    });
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "success",
    });
    // Sorting states
    const [order, setOrder] = useState("asc");
    const [orderBy, setOrderBy] = useState("id");
    // Filtering states
    const [filterText, setFilterText] = useState("");
    const [filterField, setFilterField] = useState("all");

    const navigate = useNavigate();

    useEffect(() => {
        fetchPipeWorks();
    }, []);

    // Apply filters whenever pipeWorks or filter settings change
    useEffect(() => {
        applyFilters();
    }, [pipeWorks, filterText, filterField]);

    const fetchPipeWorks = async () => {
        setLoading(true);
        try {
            console.log("Fetching pipe works...");

            const response = await apiClient.get("/api/PumpStationPriceTables/PipeWorkPrices");

            console.log("Response received successfully!");
            console.log("Data type:", typeof response.data);
            console.log("Is array:", Array.isArray(response.data));

            if (Array.isArray(response.data)) {
                setPipeWorks(response.data);
                console.log(`Successfully loaded ${response.data.length} pipe works`);
            } else {
                console.warn("Response is not an array:", response.data);
                setPipeWorks([]);
            }

            setError(null);
        } catch (err) {
            console.error("Error fetching pipe works:", err);
            setError(`Failed to load data: ${err.message}`);
            setPipeWorks([]);
        } finally {
            setLoading(false);
        }
    };

    const handleOpenDialog = (pipeWork = null) => {
        if (pipeWork) {
            setFormData({
                id: pipeWork.id || pipeWork.Id || 0,
                size: pipeWork.size || pipeWork.Size || 0,
                material: pipeWork.material || pipeWork.Material || "",
                price: pipeWork.price || pipeWork.Price || 0,
            });
            setSelectedPipeWork(pipeWork);
        } else {
            setFormData({
                id: 0,
                size: 0,
                material: "",
                price: 0,
            });
            setSelectedPipeWork(null);
        }
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
    };

    const handleOpenDeleteDialog = (pipeWork) => {
        setSelectedPipeWork(pipeWork);
        setOpenDeleteDialog(true);
    };

    const handleCloseDeleteDialog = () => {
        setOpenDeleteDialog(false);
        setSelectedPipeWork(null);
    };

    const handleFormChange = (e) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]: name === "price" || name === "size" ? parseFloat(value) || 0 : value,
        });
    };

    const handleSubmit = async () => {
        try {
            // Convert camelCase to PascalCase to match C# model property names
            const payload = {
                Id: formData.id,
                Size: formData.size,
                Material: formData.material,
                Price: formData.price,
                Quotations: [],
            };

            let response;
            if (selectedPipeWork) {
                // Update existing record
                response = await apiClient.put(
                    `/api/PumpStationPriceTables/PipeWorkPrices/${payload.Id}`,
                    payload
                );
                setSnackbar({
                    open: true,
                    message: "Pipe work updated successfully!",
                    severity: "success",
                });
            } else {
                // Create new record
                response = await apiClient.post("/api/PumpStationPriceTables/PipeWorkPrices", payload);
                setSnackbar({
                    open: true,
                    message: "Pipe work created successfully!",
                    severity: "success",
                });
            }

            handleCloseDialog();
            fetchPipeWorks();
        } catch (err) {
            console.error("Error saving pipe work:", err);
            setSnackbar({
                open: true,
                message: `Error: ${err.response?.data?.message || "Failed to save. Please try again."}`,
                severity: "error",
            });
        }
    };

    const handleDelete = async () => {
        try {
            await apiClient.delete(
                `/api/PumpStationPriceTables/PipeWorkPrices/${selectedPipeWork.id || selectedPipeWork.Id}`
            );
            setSnackbar({
                open: true,
                message: "Pipe work deleted successfully!",
                severity: "success",
            });
            handleCloseDeleteDialog();
            fetchPipeWorks();
        } catch (err) {
            console.error("Error deleting pipe work:", err);
            setSnackbar({
                open: true,
                message: `Error: ${err.response?.data?.message || "Failed to delete. Please try again."}`,
                severity: "error",
            });
        }
    };

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    // Sorting functionality
    const handleRequestSort = (property) => {
        const isAsc = orderBy === property && order === "asc";
        setOrder(isAsc ? "desc" : "asc");
        setOrderBy(property);
    };

    const getSortedData = (data) => {
        if (!data.length) return [];

        return [...data].sort((a, b) => {
            // Handle the ID/modelNo column specially
            if (orderBy === "modelNo") {
                const idA = parseInt(a.id || a.Id || 0);
                const idB = parseInt(b.id || b.Id || 0);
                return order === "asc" ? idA - idB : idB - idA;
            }

            // For any other column, get the property with both camelCase and PascalCase checks
            let valueA, valueB;

            // Try camelCase first, then PascalCase if not found
            if (orderBy === "size") {
                valueA = a.size !== undefined ? a.size : a.Size;
                valueB = b.size !== undefined ? b.size : b.Size;

                // Convert to numbers for size comparison
                valueA = parseInt(valueA) || 0;
                valueB = parseInt(valueB) || 0;
                return order === "asc" ? valueA - valueB : valueB - valueA;
            } else if (orderBy === "material") {
                valueA = a.material !== undefined ? a.material : a.Material;
                valueB = b.material !== undefined ? b.material : b.Material;
            } else if (orderBy === "price") {
                valueA = a.price !== undefined ? a.price : a.Price;
                valueB = b.price !== undefined ? b.price : b.Price;

                // Convert to numbers for price comparison
                valueA = parseFloat(valueA) || 0;
                valueB = parseFloat(valueB) || 0;
                return order === "asc" ? valueA - valueB : valueB - valueA;
            }

            // For non-numeric values, convert to strings and compare
            if (typeof valueA !== "number") {
                valueA = String(valueA || "").toLowerCase();
                valueB = String(valueB || "").toLowerCase();
                return order === "asc" ? valueA.localeCompare(valueB) : valueB.localeCompare(valueA);
            }

            // For numeric values
            return order === "asc" ? valueA - valueB : valueB - valueA;
        });
    };

    // Filtering functionality
    const handleFilterChange = (event) => {
        setFilterText(event.target.value);
    };

    const handleFilterFieldChange = (field) => {
        setFilterField(field);
    };

    const clearFilter = () => {
        setFilterText("");
        setFilterField("all");
    };

    const applyFilters = () => {
        if (!filterText.trim()) {
            setFilteredPipeWorks(pipeWorks);
            return;
        }

        const searchTerm = filterText.toLowerCase().trim();

        const filtered = pipeWorks.filter((pipeWork) => {
            // Format model number for filtering (PW-001, PW-002, etc.)
            const modelNo = `PW-${(pipeWork.id || pipeWork.Id || 0).toString().padStart(3, "0")}`;

            // Handle both camelCase and PascalCase property names from API
            const size = pipeWork.size || pipeWork.Size || 0;
            const material = pipeWork.material || pipeWork.Material || "";
            const price = pipeWork.price || pipeWork.Price || 0;

            if (filterField === "all") {
                return (
                    modelNo.toLowerCase().includes(searchTerm) ||
                    String(pipeWork.id || pipeWork.Id || "").includes(searchTerm) ||
                    String(size).includes(searchTerm) ||
                    material.toLowerCase().includes(searchTerm) ||
                    String(price).includes(searchTerm)
                );
            }

            // Filter by specific field
            if (filterField === "modelNo") return modelNo.toLowerCase().includes(searchTerm);
            if (filterField === "id") return String(pipeWork.id || pipeWork.Id || "").includes(searchTerm);
            if (filterField === "size") return String(size).includes(searchTerm);
            if (filterField === "material") return material.toLowerCase().includes(searchTerm);
            if (filterField === "price") return String(price).includes(searchTerm);

            return false;
        });

        setFilteredPipeWorks(filtered);
    };

    // Prepare data for rendering
    const displayData = getSortedData(filteredPipeWorks);

    return (
        <Box
            sx={{
                height: "100vh",
                display: "flex",
                flexDirection: "column",
                padding: 4,
                paddingTop: 2,
                boxSizing: "border-box",
                overflow: "hidden",
            }}
        >
            <Typography variant="h4" gutterBottom align="center">
                Pipe Work Price Table
            </Typography>

            <Paper
                sx={{
                    width: "100%",
                    flexGrow: 1,
                    display: "flex",
                    flexDirection: "column",
                    overflow: "hidden",
                    mb: 2,
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        p: 2,
                        alignItems: "center",
                    }}
                >
                    {/* Filter */}
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            width: "60%",
                        }}
                    >
                        <TextField
                            variant="outlined"
                            placeholder="Search..."
                            size="small"
                            fullWidth
                            value={filterText}
                            onChange={handleFilterChange}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon />
                                    </InputAdornment>
                                ),
                                endAdornment: filterText ? (
                                    <InputAdornment position="end">
                                        <IconButton size="small" onClick={clearFilter} edge="end">
                                            <ClearIcon />
                                        </IconButton>
                                    </InputAdornment>
                                ) : null,
                            }}
                        />
                        <Tooltip title="Filter by field">
                            <Box>
                                <IconButton
                                    color={filterField !== "all" ? "primary" : "default"}
                                    aria-label="Filter options"
                                    onClick={(e) => {
                                        e.currentTarget.nextElementSibling.style.display =
                                            e.currentTarget.nextElementSibling.style.display === "none"
                                                ? "flex"
                                                : "none";
                                    }}
                                >
                                    <FilterIcon />
                                </IconButton>
                                <Box
                                    sx={{
                                        position: "absolute",
                                        zIndex: 10,
                                        bgcolor: "background.paper",
                                        boxShadow: 3,
                                        borderRadius: 1,
                                        p: 1,
                                        display: "none",
                                        flexDirection: "column",
                                        gap: 0.5,
                                    }}
                                >
                                    {[
                                        { id: "all", label: "All Fields" },
                                        { id: "modelNo", label: "ID" },
                                        { id: "size", label: "Size" },
                                        { id: "material", label: "Material" },
                                        { id: "price", label: "Price" },
                                    ].map((option) => (
                                        <Button
                                            key={option.id}
                                            size="small"
                                            variant={filterField === option.id ? "contained" : "text"}
                                            onClick={() => {
                                                handleFilterFieldChange(option.id);
                                                // Hide the filter dropdown
                                                document.querySelector(
                                                    '[aria-label="Filter options"]'
                                                ).nextElementSibling.style.display = "none";
                                            }}
                                        >
                                            {option.label}
                                        </Button>
                                    ))}
                                </Box>
                            </Box>
                        </Tooltip>
                        {filterField !== "all" && (
                            <Typography variant="caption" sx={{ ml: 1 }}>
                                Filtering by:{" "}
                                {filterField === "modelNo"
                                    ? "ID"
                                    : filterField === "id"
                                    ? "ID"
                                    : filterField === "size"
                                    ? "Size"
                                    : filterField === "material"
                                    ? "Material"
                                    : filterField === "price"
                                    ? "Price"
                                    : ""}
                            </Typography>
                        )}
                    </Box>

                    {/* Add button */}
                    <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog()}>
                        Add New Item
                    </Button>
                </Box>

                <Divider />

                {loading ? (
                    <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
                        <CircularProgress />
                    </Box>
                ) : error ? (
                    <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
                        <Typography color="error">{error}</Typography>
                    </Box>
                ) : (
                    <>
                        <TableContainer
                            sx={{
                                flex: 1,
                                overflow: "auto",
                            }}
                        >
                            <Table stickyHeader aria-label="pipe work price table">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>
                                            <TableSortLabel
                                                active={orderBy === "modelNo"}
                                                direction={orderBy === "modelNo" ? order : "asc"}
                                                onClick={() => handleRequestSort("modelNo")}
                                            >
                                                <strong>ID</strong>
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell>
                                            <TableSortLabel
                                                active={orderBy === "size"}
                                                direction={orderBy === "size" ? order : "asc"}
                                                onClick={() => handleRequestSort("size")}
                                            >
                                                <strong>Size</strong>
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell>
                                            <TableSortLabel
                                                active={orderBy === "material"}
                                                direction={orderBy === "material" ? order : "asc"}
                                                onClick={() => handleRequestSort("material")}
                                            >
                                                <strong>Material</strong>
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell>
                                            <TableSortLabel
                                                active={orderBy === "price"}
                                                direction={orderBy === "price" ? order : "asc"}
                                                onClick={() => handleRequestSort("price")}
                                            >
                                                <strong>Price</strong>
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell align="center">
                                            <strong>Actions</strong>
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {displayData.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={5} align="center">
                                                {filterText
                                                    ? "No matches found. Try a different search."
                                                    : "No data available. Add your first pipe work."}
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        displayData.map((pipeWork) => (
                                            <TableRow key={pipeWork.id || pipeWork.Id} hover>
                                                <TableCell>
                                                    PW-
                                                    {(pipeWork.id || pipeWork.Id).toString().padStart(3, "0")}
                                                </TableCell>
                                                <TableCell>{pipeWork.size || pipeWork.Size}</TableCell>
                                                <TableCell>
                                                    {pipeWork.material || pipeWork.Material}
                                                </TableCell>
                                                <TableCell>
                                                    $
                                                    {typeof (pipeWork.price || pipeWork.Price) === "number"
                                                        ? (pipeWork.price || pipeWork.Price).toFixed(2)
                                                        : "0.00"}
                                                </TableCell>
                                                <TableCell align="center">
                                                    <IconButton
                                                        color="primary"
                                                        onClick={() => handleOpenDialog(pipeWork)}
                                                        aria-label="edit"
                                                    >
                                                        <EditIcon />
                                                    </IconButton>
                                                    <IconButton
                                                        color="error"
                                                        onClick={() => handleOpenDeleteDialog(pipeWork)}
                                                        aria-label="delete"
                                                    >
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <Box
                            sx={{
                                p: 2,
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                            }}
                        >
                            <Typography variant="body2" color="text.secondary">
                                {filterText
                                    ? `Showing ${displayData.length} of ${pipeWorks.length} items`
                                    : `Total: ${pipeWorks.length} items`}
                            </Typography>
                            {filterText && (
                                <Button size="small" startIcon={<ClearIcon />} onClick={clearFilter}>
                                    Clear Filter
                                </Button>
                            )}
                        </Box>
                    </>
                )}
            </Paper>

            <Box sx={{ display: "flex", justifyContent: "center", gap: 2 }}>
                <BackToCPQButton />
                <PreviousPageButtonCPQ />
            </Box>

            {/* Create/Edit Dialog */}
            <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
                <DialogTitle>{selectedPipeWork ? "Edit Pipe Work" : "Add New Pipe Work"}</DialogTitle>
                <DialogContent>
                    <Box component="form" sx={{ mt: 2 }}>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Size"
                                    name="size"
                                    type="number"
                                    inputProps={{ min: "0" }}
                                    value={formData.size}
                                    onChange={handleFormChange}
                                    required
                                    variant="outlined"
                                    helperText="Pipe size in mm"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Material"
                                    name="material"
                                    value={formData.material}
                                    onChange={handleFormChange}
                                    required
                                    variant="outlined"
                                    helperText="e.g., PVC, Steel, HDPE"
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Price"
                                    name="price"
                                    type="number"
                                    inputProps={{ step: "0.01", min: "0" }}
                                    value={formData.price}
                                    onChange={handleFormChange}
                                    required
                                    variant="outlined"
                                />
                            </Grid>
                        </Grid>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog}>Cancel</Button>
                    <Button onClick={handleSubmit} variant="contained" color="primary">
                        {selectedPipeWork ? "Update" : "Save"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={openDeleteDialog} onClose={handleCloseDeleteDialog}>
                <DialogTitle>Confirm Delete</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete this pipe work? This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDeleteDialog}>Cancel</Button>
                    <Button onClick={handleDelete} color="error" variant="contained">
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar for notifications */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert
                    onClose={handleCloseSnackbar}
                    severity={snackbar.severity}
                    variant="filled"
                    sx={{ width: "100%" }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}

export default PipeWorkPriceTable;
